import { handleErrorToast } from 'src/components/molecules';
import { readableFileSize } from 'src/components/molecules/FileUpload/helpers';
import { DeletedEvidenceFile } from 'src/components/molecules/UploadedEvidence';
import { useGetReportUploadReadUrlForReporterMutation } from 'src/graphql';
import { UploadScanStatus } from 'src/types';
import { canDownloadFile, getStatusColumnText } from 'src/utils';
import { DateFormats, formatDate } from 'src/utils/time';
import {
  EvidenceTitle,
  FileNameText,
  FilesTable,
  FlaggedFilesError,
  FlaggedFilesMobileError,
  TableCell,
  TableFileNameCellLink,
  TableHeadingRow,
  TableRow,
  UploadedEvidenceContainer,
} from './ReportingReport.styles';
import { FileUploadEvidenceContentType } from './helpers';

type LocalisedText = {
  tableTitle: string;
  webErrorMessage: string;
  mobileErrorMessage: string;
  nameHeading: string;
  addedHeading: string;
  statusHeading: string;
  failedToOpenFileMessage: string;
  fileStatuses: {
    pendingUpload: string;
    passedUpload: string;
    infectedUpload: string;
    readyForDownload: string;
  };
};

export type UploadedEvidenceFile = {
  size?: number;
  dateAdded: Date;
  status: UploadScanStatus;
  fileExtension: string;
  fileS3Path?: string;
  directFileUrl?: string;
  dataDeletedAt?: string | null;
};

const generateFileNameText = (
  baseFileName: string,
  index: number,
  file: FileUploadEvidenceContentType,
) => {
  const defaultFileName = `${baseFileName}${index + 1}`;
  return {
    defaultFileName,
    fileNameText: `${defaultFileName}.${file.fileExtension}${
      file.size ? ` ${readableFileSize(file.size)}` : ''
    }`,
  };
};

interface Props {
  files: FileUploadEvidenceContentType[];
  reportId: string;
  caseManagerView?: boolean;
  localisedText: LocalisedText;
  'data-testid'?: string;
  hideTitle?: boolean;
  baseFileName?: string;
  downloadEvidenceDisabled?: boolean;
}

export const UploadedEvidence = ({
  files,
  caseManagerView = false,
  localisedText,
  reportId,
  hideTitle,
  baseFileName = 'EvidenceUploaded',
  downloadEvidenceDisabled,
  ...props
}: Props) => {
  const dateFormat = DateFormats.DAY_MONTH_YEAR;
  const hasFlaggedFiles = files.some(
    (file) => file.status === UploadScanStatus.INFECTED,
  );
  const getReportDocumentUploadURL =
    useGetReportUploadReadUrlForReporterMutation();

  const getOpenFileUrl = async (file: UploadedEvidenceFile) => {
    if (file.directFileUrl) {
      return file.directFileUrl;
    }

    if (file.fileS3Path) {
      const {
        getReportUploadReadURLForReporter: { url },
      } = await getReportDocumentUploadURL.mutateAsync({
        reportId,
        objectId: file.fileS3Path,
      });
      return url;
    }

    return '';
  };

  const handleOpenFile = async (file: UploadedEvidenceFile) => {
    if (downloadEvidenceDisabled) return;
    if (!canDownloadFile(file.status)) return;

    try {
      const url = await getOpenFileUrl(file);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      handleErrorToast({
        toastMessage: localisedText.failedToOpenFileMessage,
      });
    }
  };

  const showTitle = !hideTitle;
  const canDownloadEvidence = !downloadEvidenceDisabled;

  const isFileDeleted = (file: FileUploadEvidenceContentType) => {
    return (
      file.dataDeletedAt || (file.fileS3Path?.includes('_deleted') ?? false)
    );
  };

  return (
    <UploadedEvidenceContainer data-testid={props['data-testid']}>
      {showTitle && <EvidenceTitle>{localisedText.tableTitle}</EvidenceTitle>}
      {hasFlaggedFiles && (
        <>
          <FlaggedFilesError data-testid="flagged-files-error">
            {localisedText.webErrorMessage}
          </FlaggedFilesError>
          <FlaggedFilesMobileError>
            {localisedText.mobileErrorMessage}
          </FlaggedFilesMobileError>
        </>
      )}
      <div>
        <FilesTable>
          <thead>
            <TableHeadingRow>
              <td>{localisedText.nameHeading}</td>
              <td>{localisedText.addedHeading}</td>
              <td>{localisedText.statusHeading}</td>
            </TableHeadingRow>
          </thead>
          <tbody>
            {files.map((file, index) => {
              const { defaultFileName, fileNameText } = generateFileNameText(
                baseFileName,
                index,
                file,
              );

              if (isFileDeleted(file)) {
                return (
                  <DeletedEvidenceFile
                    key={`deleted-${file.fileS3Path || 'file'}-${index}`}
                    file={file}
                    index={index}
                    baseFileName={baseFileName}
                  />
                );
              }

              const scanStatus = getStatusColumnText(
                file.status,
                localisedText.fileStatuses,
                downloadEvidenceDisabled,
                !caseManagerView,
              );

              return (
                <TableRow
                  key={index}
                  scanStatus={file.status}
                  data-testid={`uploaded-file-${defaultFileName}`}
                >
                  <td data-testid={`uploaded-evidence-file-name-${index}`}>
                    {canDownloadEvidence ? (
                      <TableFileNameCellLink
                        onClick={() => handleOpenFile(file)}
                        data-testid={`uploaded-evidence-file-link-${index}`}
                        scanStatus={file.status}
                      >
                        {fileNameText}
                      </TableFileNameCellLink>
                    ) : (
                      <FileNameText scanStatus={file.status}>
                        {fileNameText}
                      </FileNameText>
                    )}
                  </td>
                  <TableCell
                    data-testid={`uploaded-evidence-file-date-${index}`}
                  >
                    {formatDate(file.dateAdded, dateFormat)}
                  </TableCell>
                  <TableCell
                    data-testid={`uploaded-evidence-file-status-${index}`}
                  >
                    {scanStatus}
                  </TableCell>
                </TableRow>
              );
            })}
          </tbody>
        </FilesTable>
      </div>
    </UploadedEvidenceContainer>
  );
};
